import { useState } from "react";

const BOOK_URL = "https://calendly.com/welcome-runwaycrew/30min";
const API = "https://api.runwaycrew.com";

/* ---------- Icons ---------- */
const ArrowRight = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);
const Check = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);
const X = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const Phone = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 8V5z" />
  </svg>
);
const MessageSquare = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);
const Mail = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);
const Globe = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" />
  </svg>
);
const Users = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const Zap = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);
const Inbox = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

/* ---------- Nav ---------- */
function NavDropdown({ onLegacyLogin }: { onLegacyLogin: () => void }) {
  const [open, setOpen] = useState(false);
  const sections = [
    { id: "problem", label: "The Problem" },
    { id: "solution", label: "The Solution" },
    { id: "how", label: "How It Works" },
    { id: "value", label: "The Value" },
    { id: "pricing", label: "Pricing" },
    { id: "cta", label: "Book a Demo" },
  ];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2 rounded border border-stone-600 text-stone-300 hover:border-stone-400 hover:text-white text-sm font-medium transition-colors"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        Menu
        <svg className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.062l-4.24 4.24a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 bg-white border border-stone-200 rounded-xl shadow-2xl p-2 z-50">
          {sections.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 rounded-lg text-stone-700 hover:bg-stone-100 text-sm transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {s.label}
            </a>
          ))}
          <div className="my-1.5 border-t border-stone-200" />
          <button
            onClick={() => { window.location.assign("https://login.runwaycrew.com/"); setOpen(false); }}
            className="w-full text-left px-3 py-2 rounded-lg text-sm font-semibold text-stone-700 hover:bg-stone-100 transition-colors"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Client Login
          </button>
          <button
            onClick={() => { onLegacyLogin(); setOpen(false); }}
            className="w-full text-left px-3 py-2 rounded-lg text-xs text-stone-400 hover:bg-stone-100 transition-colors"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Legacy Login
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------- Login Modal ---------- */
function LoginModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || "Invalid email or password");
      }
      window.location.assign("/dashboard");
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-stone-900/70 grid place-items-center p-4 z-50 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-white border border-stone-200 rounded-2xl shadow-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Client Login
          </h2>
          <button className="text-stone-400 hover:text-stone-700 transition-colors" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>
        {msg && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">{msg}</div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1.5 text-stone-700" style={{ fontFamily: "'DM Sans', sans-serif" }}>Email</label>
            <input
              type="email"
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-2.5 text-stone-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
              value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-1.5 text-stone-700" style={{ fontFamily: "'DM Sans', sans-serif" }}>Password</label>
            <input
              type="password"
              className="w-full bg-stone-50 border border-stone-300 rounded-lg px-4 py-2.5 text-stone-900 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all"
              value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
            />
          </div>
          <button
            className="w-full bg-stone-900 text-white py-3 rounded-lg font-bold hover:bg-stone-700 disabled:opacity-50 transition-colors"
            style={{ fontFamily: "'DM Sans', sans-serif" }} disabled={busy}
          >
            {busy ? "Signing in…" : "Sign In →"}
          </button>
        </form>
        <p className="mt-4 text-xs text-center text-stone-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
          Trouble logging in? Contact your Runway Crew advisor.
        </p>
      </div>
    </div>
  );
}

/* ---------- Booking Modal ---------- */
function BookingModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-stone-900/70 grid place-items-center p-4 z-50 backdrop-blur-sm">
      <div className="w-full max-w-4xl h-[85vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            Book a Demo
          </h2>
          <button className="text-stone-400 hover:text-stone-700 transition-colors" onClick={onClose}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1">
          <iframe src={BOOK_URL} width="100%" height="100%" title="Book a Demo" />
        </div>
      </div>
    </div>
  );
}

/* ---------- Main Page ---------- */
export default function LandingPage() {
  const [showBooking, setShowBooking] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Sans:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <style>{`
        html { scroll-behavior: smooth; }
        .hero-line {
          background: linear-gradient(135deg, #1c1917 0%, #44403c 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .accent-line {
          background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .stat-card { position: relative; overflow: hidden; }
        .stat-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, #b45309, #d97706);
        }
        .book-btn {
          background: linear-gradient(135deg, #1c1917 0%, #292524 100%);
          position: relative;
          overflow: hidden;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .book-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.25);
        }
        .book-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.05) 0%, transparent 100%);
        }
        .problem-card { transition: transform 0.2s, box-shadow 0.2s; }
        .problem-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
        }
        .feature-card { transition: transform 0.2s, box-shadow 0.2s; }
        .feature-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.07);
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .pulse-dot { animation: pulse-dot 2s ease-in-out infinite; }

        /* Inbox mockup */
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .inbox-row { animation: slide-in 0.4s ease forwards; }
      `}</style>

      <div className="min-h-screen bg-stone-50 relative">
        <div style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 100, opacity: 0.4,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E")`,
        }} />

        {/* ── HEADER ── */}
        <header className="sticky top-0 z-30 bg-stone-900/95 backdrop-blur-md border-b border-stone-800">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <a href="#hero" className="flex items-center gap-2">
              <img src="/runway-crew-logo-v2.png" alt="Runway Crew" className="h-9 w-auto" />
            </a>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowBooking(true)}
                className="hidden sm:flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg transition-colors"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Book a Demo
              </button>
              <NavDropdown onLegacyLogin={() => setShowLogin(true)} />
            </div>
          </div>
        </header>

        <main>

          {/* ── HERO ── */}
          <section id="hero" className="relative overflow-hidden scroll-mt-20 bg-white">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,_rgba(180,83,9,0.06)_0%,_transparent_100%)]" />
            <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-16">
              {/* ── CHANGED: badge copy ── */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full mb-8">
                <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-amber-700 uppercase tracking-widest" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  For Law Firms That Can&apos;t Afford to Miss a Lead
                </span>
              </div>

              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  {/* ── CHANGED: headline ── */}
                  <h1 className="text-5xl md:text-6xl font-black leading-[1.02] mb-6" style={{ fontFamily: "'Playfair Display', serif", letterSpacing: "-0.02em" }}>
                    <span className="hero-line">Never Miss</span><br />
                    <span className="hero-line">Another </span>
                    <span className="accent-line">Inbound Lead.</span>
                  </h1>
                  <p className="text-xl text-stone-600 max-w-lg mb-3 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                    All your calls, texts, emails, and chats — organized in one simple inbox.
                  </p>
                  {/* ── CHANGED: subhead ── */}
                  <p className="text-base text-stone-500 max-w-lg mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                    A lead comes in. It shows up here. It gets handled.
                  </p>
                  <p className="text-lg font-bold text-stone-800 mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
                    That&apos;s the whole system.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => setShowBooking(true)}
                      className="book-btn group px-8 py-4 text-white rounded-xl font-bold text-lg inline-flex items-center gap-3"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      Book a Demo
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>

                {/* Inbox visual mockup */}
                <div className="hidden lg:block">
                  <div className="bg-white border border-stone-200 rounded-2xl shadow-xl overflow-hidden">
                    <div className="bg-stone-900 px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Inbox className="w-4 h-4 text-amber-400" />
                        <span className="text-white text-sm font-bold" style={{ fontFamily: "'DM Sans', sans-serif" }}>Lead Inbox</span>
                      </div>
                      <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">4 new</span>
                    </div>
                    <div className="divide-y divide-stone-100">
                      {[
                        { type: "call", icon: <Phone className="w-4 h-4" />, color: "text-green-600 bg-green-50", name: "James R.", detail: "Missed call + voicemail", time: "2m ago", badge: "New", badgeColor: "bg-amber-100 text-amber-700" },
                        { type: "text", icon: <MessageSquare className="w-4 h-4" />, color: "text-blue-600 bg-blue-50", name: "Maria T.", detail: "Injury case inquiry", time: "11m ago", badge: "New", badgeColor: "bg-amber-100 text-amber-700" },
                        { type: "email", icon: <Mail className="w-4 h-4" />, color: "text-purple-600 bg-purple-50", name: "D. Kowalski", detail: "Free consultation request", time: "34m ago", badge: "New", badgeColor: "bg-amber-100 text-amber-700" },
                        { type: "chat", icon: <Globe className="w-4 h-4" />, color: "text-orange-600 bg-orange-50", name: "Web visitor", detail: "\"How much does it cost?\"", time: "1h ago", badge: "New", badgeColor: "bg-amber-100 text-amber-700" },
                        { type: "call", icon: <Phone className="w-4 h-4" />, color: "text-green-600 bg-green-50", name: "Sarah M.", detail: "Follow-up scheduled", time: "3h ago", badge: "Handled", badgeColor: "bg-stone-100 text-stone-500" },
                      ].map((row, i) => (
                        <div key={i} className="inbox-row flex items-center gap-4 px-5 py-3.5 hover:bg-stone-50 transition-colors" style={{ animationDelay: `${i * 0.08}s` }}>
                          <div className={`w-8 h-8 rounded-lg ${row.color} flex items-center justify-center flex-shrink-0`}>
                            {row.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-semibold text-stone-800 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{row.name}</span>
                              <span className="text-xs text-stone-400 flex-shrink-0" style={{ fontFamily: "'DM Sans', sans-serif" }}>{row.time}</span>
                            </div>
                            <p className="text-xs text-stone-500 truncate" style={{ fontFamily: "'DM Sans', sans-serif" }}>{row.detail}</p>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${row.badgeColor}`} style={{ fontFamily: "'DM Sans', sans-serif" }}>{row.badge}</span>
                        </div>
                      ))}
                    </div>
                    {/* ── CHANGED: inbox footer ── */}
                    <div className="px-5 py-3 bg-stone-50 border-t border-stone-100 text-xs text-stone-400 text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      All leads from every channel — in one place
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── THE PROBLEM ── */}
          <section id="problem" className="py-24 px-6 bg-stone-900 scroll-mt-20">
            <div className="max-w-5xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>The Real Problem</div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Leads are slipping through<br />
                <span className="text-amber-400">the cracks.</span>
              </h2>
              {/* ── CHANGED: problem intro ── */}
              <p className="text-stone-400 text-lg mb-8 max-w-2xl" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                Leads come in from everywhere. The problem? They all land in different places.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
                {[
                  { icon: <Phone className="w-5 h-5" />, label: "Phone calls" },
                  { icon: <Globe className="w-5 h-5" />, label: "Website chats" },
                  { icon: <Mail className="w-5 h-5" />, label: "Emails" },
                  { icon: <MessageSquare className="w-5 h-5" />, label: "Text messages" },
                ].map(({ icon, label }) => (
                  <div key={label} className="flex items-center gap-3 p-4 bg-stone-800 border border-stone-700 rounded-xl">
                    <div className="w-9 h-9 rounded-lg bg-amber-600/20 border border-amber-600/30 flex items-center justify-center text-amber-400 flex-shrink-0">{icon}</div>
                    <span className="font-semibold text-white text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
                  </div>
                ))}
              </div>

              <p className="text-stone-400 text-base mb-8 max-w-xl" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                They all live in different places. That means:
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-12">
                {[
                  {
                    icon: "✗",
                    title: "Missed follow-ups",
                    body: "A lead comes in on a Friday. Someone meant to call back Monday. It never happens. That case is gone.",
                  },
                  {
                    icon: "✗",
                    title: "Delayed responses",
                    body: "You called back 4 hours later. They already hired someone else. Response time wins cases.",
                  },
                  {
                    icon: "✗",
                    title: "Lost cases",
                    body: "You paid for the lead. You got the lead. But nobody knew where it went — so it went nowhere.",
                  },
                ].map(({ icon, title, body }) => (
                  <div key={title} className="problem-card p-6 bg-stone-800 border border-stone-700 rounded-xl">
                    <div className="w-8 h-8 rounded-lg bg-red-900/30 border border-red-700/40 flex items-center justify-center text-red-400 font-black text-lg mb-4">{icon}</div>
                    <h3 className="font-bold text-white mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>{title}</h3>
                    <p className="text-stone-400 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{body}</p>
                  </div>
                ))}
              </div>

              <div className="p-6 bg-amber-600/10 border border-amber-600/20 rounded-xl">
                <p className="text-amber-200 text-lg leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                  You&apos;re paying for leads… but not converting all of them.
                </p>
              </div>
            </div>
          </section>

          {/* ── THE SOLUTION ── */}
          <section id="solution" className="py-24 px-6 bg-white scroll-mt-20">
            <div className="max-w-5xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>The Solution</div>
              <h2 className="text-4xl md:text-5xl font-black text-stone-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                One inbox<br />
                <span className="accent-line">for everything.</span>
              </h2>
              {/* ── CHANGED: solution intro ── */}
              <p className="text-stone-500 text-lg mb-4 max-w-2xl" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                We bring every inbound lead into one place — calls, texts, emails, and web chat — organized into a single, easy-to-use inbox.
              </p>
              <p className="text-stone-700 font-semibold text-base mb-14 max-w-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Just like email… but for your entire intake.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: <Phone className="w-5 h-5" />,
                    title: "Calls",
                    body: "Every inbound call shows up in your inbox — with recordings and voicemails attached. No more digging through your phone or missing a message a staff member forgot to relay.",
                    tag: "Recordings + voicemails",
                  },
                  {
                    icon: <MessageSquare className="w-5 h-5" />,
                    title: "Text Messages",
                    body: "Text leads land directly in your inbox, ready to reply to. You see the full thread, the context, and the history — all in one place.",
                    tag: "Full thread history",
                  },
                  {
                    icon: <Mail className="w-5 h-5" />,
                    title: "Emails",
                    body: "Form submissions and email inquiries flow right in. No more checking a separate inbox or wondering if someone already responded.",
                    tag: "Zero inbox-switching",
                  },
                  {
                    icon: <Globe className="w-5 h-5" />,
                    title: "Web Chat",
                    body: "Live chat and chatbot conversations from your website get captured and organized just like every other lead — nothing falls through.",
                    tag: "Every channel covered",
                  },
                ].map(({ icon, title, body, tag }, i) => (
                  <div key={title} className="feature-card p-8 border border-stone-200 rounded-xl bg-stone-50 relative overflow-hidden">
                    <div className="absolute top-6 right-6 text-5xl font-black text-stone-100 leading-none select-none" style={{ fontFamily: "'Playfair Display', serif" }}>0{i + 1}</div>
                    <div className="w-10 h-10 rounded-lg bg-stone-900 flex items-center justify-center text-white mb-5 relative z-10">{icon}</div>
                    <h3 className="text-xl font-bold text-stone-900 mb-3 relative z-10" style={{ fontFamily: "'DM Sans', sans-serif" }}>{title}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed mb-4 relative z-10" style={{ fontFamily: "'DM Sans', sans-serif" }}>{body}</p>
                    <span className="inline-block px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold rounded-full relative z-10" style={{ fontFamily: "'DM Sans', sans-serif" }}>{tag}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── HOW IT WORKS ── */}
          <section id="how" className="py-24 px-6 bg-stone-50 border-t border-stone-200 scroll-mt-20">
            <div className="max-w-4xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Process</div>
              <h2 className="text-4xl md:text-5xl font-black text-stone-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Stupid simple.<br />
                <span className="accent-line">By design.</span>
              </h2>
              <p className="text-stone-500 text-lg mb-14 max-w-xl" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                Four steps. That&apos;s it.
              </p>
              <div className="space-y-4">
                {[
                  {
                    step: "Step 1",
                    // ── CHANGED: step 1 title + body ──
                    title: "A lead comes in from any channel",
                    body: "A call, text, email, or chat arrives — from a directory, referral, or your own website. Doesn't matter where it came from.",
                  },
                  {
                    step: "Step 2",
                    title: "It appears in your inbox",
                    body: "No switching systems. No guessing where it went. It's just there, in one place, with full context.",
                  },
                  {
                    step: "Step 3",
                    title: "You respond or assign it",
                    body: "Quickly reply, add notes, or route it to the right attorney. Everyone on your team sees the same thing.",
                  },
                  {
                    step: "Step 4",
                    title: "Mark it handled",
                    body: "So nothing gets missed or forgotten. You always know exactly where every lead stands.",
                  },
                ].map(({ step, title, body }, i) => (
                  <div key={step} className="flex gap-6 p-6 bg-white border border-stone-200 rounded-xl">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center font-black text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{i + 1}</div>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{step}</div>
                      <h3 className="font-bold text-stone-900 mb-1" style={{ fontFamily: "'DM Sans', sans-serif" }}>{title}</h3>
                      <p className="text-stone-500 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── WHAT MAKES IT POWERFUL ── */}
          <section className="py-24 px-6 bg-white border-t border-stone-200 scroll-mt-20">
            <div className="max-w-5xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Why It Works</div>
              <h2 className="text-4xl md:text-5xl font-black text-stone-900 mb-14 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                What makes this<br />
                <span className="accent-line">powerful.</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: <Inbox className="w-5 h-5" />,
                    title: "Everything in One Place",
                    body: "No more bouncing between your phone, email, CRM, and chat tool. One screen shows you everything — and everyone on your team sees the same thing.",
                  },
                  {
                    icon: <Zap className="w-5 h-5" />,
                    title: "Built for Speed",
                    body: "Respond faster than competitors. When a lead reaches out, you reply in minutes — not hours. That speed alone wins cases.",
                  },
                  {
                    icon: <Phone className="w-5 h-5" />,
                    title: "Full Context",
                    body: "Call recordings, voicemail transcripts, and message history all together — so whoever picks up the conversation knows exactly what's been said.",
                  },
                  {
                    icon: <Users className="w-5 h-5" />,
                    title: "Team Visibility",
                    body: "Everyone sees what's been handled — and what hasn't. No more \"I thought you called them back\" moments. Clear ownership, every time.",
                  },
                ].map(({ icon, title, body }) => (
                  <div key={title} className="feature-card p-8 border border-stone-200 rounded-xl bg-stone-50">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700">{icon}</div>
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-green-500" />
                        <h3 className="font-bold text-stone-900" style={{ fontFamily: "'DM Sans', sans-serif" }}>{title}</h3>
                      </div>
                    </div>
                    <p className="text-stone-500 text-sm leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── THE VALUE / ROI ── */}
          <section id="value" className="py-24 px-6 bg-stone-900 scroll-mt-20">
            <div className="max-w-4xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-500 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>The Real Value</div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                What is one missed case worth?
              </h2>
              <p className="text-stone-300 text-xl mb-2 font-light" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                $3,000? $5,000? $10,000+?
              </p>
              <p className="text-stone-400 text-base mb-14 max-w-lg" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                If this system helps you save just 1–2 missed leads per month, it pays for itself instantly.
              </p>

              <div className="grid md:grid-cols-3 gap-4 mb-12">
                {[
                  { stat: "1–2", label: "missed cases saved per month covers the entire cost" },
                  { stat: "$0", label: "setup fees, contracts, or hidden add-ons" },
                  { stat: "< 1 wk", label: "to be fully live inside your firm" },
                ].map(({ stat, label }) => (
                  <div key={stat} className="stat-card bg-stone-800 border border-stone-700 rounded-xl p-6">
                    <div className="text-4xl font-black text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{stat}</div>
                    <div className="text-sm text-stone-400 leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>{label}</div>
                  </div>
                ))}
              </div>

              <div className="p-7 bg-stone-800 border border-stone-700 rounded-xl mb-6">
                <p className="text-white font-black text-xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Why not just hire another receptionist?</p>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <p className="text-stone-400 text-xs font-bold uppercase tracking-widest mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Hiring a receptionist costs:</p>
                    <ul className="space-y-2">
                      {["$3K–$5K per month in salary", "Weeks of training", "Ongoing management time", "Sick days and coverage gaps"].map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-stone-400" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                          <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Runway Crew gives you:</p>
                    <ul className="space-y-2">
                      {["The structure of a full intake team", "No headcount added", "No training required", "Running in under a week"].map((item) => (
                        <li key={item} className="flex items-center gap-2 text-sm text-stone-300" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                          <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-amber-600/10 border border-amber-600/20 rounded-xl">
                <p className="text-amber-200 text-lg leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                  &quot;The leads you&apos;re already receiving are worth more than your current close rate suggests. Let&apos;s fix that.&quot;
                </p>
              </div>
            </div>
          </section>

          {/* ── PRICING ── */}
          <section id="pricing" className="py-24 px-6 bg-stone-50 border-t border-stone-200 scroll-mt-20">
            <div className="max-w-4xl mx-auto">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Investment</div>
              <h2 className="text-4xl md:text-5xl font-black text-stone-900 mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                Simple pricing.<br />
                <span className="accent-line">Everything included.</span>
              </h2>
              <p className="text-stone-500 text-lg mb-14 max-w-xl" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                No contracts. Cancel anytime.
              </p>
              <div className="p-10 bg-stone-900 rounded-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(180,83,9,0.15)_0%,_transparent_60%)]" />
                <div className="relative grid md:grid-cols-2 gap-10 items-start">
                  <div>
                    <div className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-4" style={{ fontFamily: "'DM Sans', sans-serif" }}>Monthly Retainer</div>
                    <div className="flex items-end gap-2 mb-1">
                      <div className="text-6xl font-black text-white leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>$2,000</div>
                      <div className="text-stone-400 text-lg mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>/mo</div>
                    </div>
                    <div className="text-stone-500 text-sm mb-8" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                      Flat monthly fee. No setup costs. No surprise add-ons.
                    </div>
                    <div className="space-y-3 mb-8">
                      {[
                        "All inbound leads in one inbox",
                        "Call recordings + voicemail tracking",
                        "Text + email + chat management",
                        "Team collaboration + assignment",
                        "Lead source attribution by channel",
                        "Full pipeline — New Lead through Active Client",
                        "Ongoing support + optimization",
                        "Direct access — no tickets, no account manager",
                      ].map((item) => (
                        <div key={item} className="flex items-start gap-3 text-sm text-stone-300" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                          <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                          {item}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowBooking(true)}
                      className="group w-full py-4 bg-amber-500 hover:bg-amber-400 text-stone-900 rounded-xl font-bold text-base transition-colors inline-flex items-center justify-center gap-2"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      Book a Demo
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div className="p-5 bg-stone-800 border border-stone-700 rounded-xl">
                      <div className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>Who this is for</div>
                      <ul className="space-y-2 text-sm text-stone-300" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                        {[
                          // ── CHANGED: first bullet ──
                          "Firms investing in lead generation who want to convert more of what comes in",
                          "Practices where calls sometimes go unanswered or untracked",
                          "Attorneys who want a simple, organized intake process",
                          "Firms that want every lead handled — without adding staff",
                        ].map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <ArrowRight className="w-3 h-3 text-amber-500 mt-1 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="p-5 bg-amber-600/10 border border-amber-600/20 rounded-xl">
                      <p className="text-stone-300 text-sm font-semibold mb-2" style={{ fontFamily: "'DM Sans', sans-serif" }}>No contracts. Cancel anytime.</p>
                      <p className="text-stone-400 text-xs leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                        We earn your business every month. If it&apos;s not working, you can cancel. But it will work — and you&apos;ll know it&apos;s working because you&apos;ll see every lead that comes in, and every lead that converts.
                      </p>
                    </div>
                    <div className="p-5 bg-stone-800 border border-stone-700 rounded-xl">
                      <p className="text-amber-300 text-sm italic leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                        &quot;Stop losing cases you already paid for.&quot;
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── FINAL CTA ── */}
          <section id="cta" className="py-32 px-6 bg-stone-50 border-t border-stone-200 scroll-mt-20">
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-6" style={{ fontFamily: "'DM Sans', sans-serif" }}>No pitch. Just a conversation.</div>
              <h2 className="text-5xl md:text-6xl font-black text-stone-900 mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                If a lead comes in…<br />
                <span className="accent-line">it gets handled.</span>
              </h2>
              <p className="text-xl text-stone-500 mb-4 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>
                That&apos;s the system. Stop losing cases you already paid for.
              </p>
              <p className="text-base text-stone-400 mb-12" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                30-minute demo. We&apos;ll walk through your current intake, show you exactly where leads are slipping through, and show you what the inbox looks like in practice.
              </p>
              <button
                onClick={() => setShowBooking(true)}
                className="book-btn group px-12 py-5 text-white rounded-xl font-bold text-xl inline-flex items-center gap-3"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Book a Demo
                <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
              </button>
              <p className="mt-6 text-stone-400 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                Or email directly: <span className="text-stone-600 font-medium">welcome@runwaycrew.com</span>
              </p>
            </div>
          </section>
        </main>

        {/* ── FOOTER ── */}
        <footer className="bg-stone-900 border-t border-stone-800 py-10 px-6">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <img src="/runway-crew-logo-v2.png" alt="Runway Crew" className="h-8 w-auto" />
            <p className="text-stone-500 text-sm text-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Law firm intake &amp; lead conversion. Founder-operated.
            </p>
            <p className="text-stone-600 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              © {new Date().getFullYear()} Runway Crew. All rights reserved.
            </p>
          </div>
        </footer>
      </div>

      {showBooking && <BookingModal onClose={() => setShowBooking(false)} />}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
    </>
  );
}